/**
 * ProviderRail — left rail for the provider dialog (same look as the account
 * wizard's WizardRail): grouped list of every possible transport, each with a
 * ✓ tile when configured. Selecting an item shows its config on the right.
 */

import React from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  Check as CheckIcon,
  KeyboardArrowRight as CollapsedIcon,
  KeyboardArrowDown as ExpandedIcon,
} from "@mui/icons-material";

export interface ProviderRailItem {
  key: string;
  title: string;
  configured: boolean;
}

export interface ProviderRailGroup {
  label?: string;
  items: ProviderRailItem[];
  /** When true the group header toggles show/hide of its items. */
  collapsible?: boolean;
}

export interface ProviderRailProps {
  groups: ProviderRailGroup[];
  selected: string;
  onSelect: (key: string) => void;
}

export const ProviderRail: React.FC<ProviderRailProps> = ({
  groups,
  selected,
  onSelect,
}) => {
  const theme = useTheme();
  const rg = theme.custom.rg;
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  return (
    <Box
      sx={{
        width: 236,
        flex: "none",
        backgroundColor: rg.bg2,
        borderRight: `1px solid ${rg.border}`,
        p: "14px 12px",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        overflowY: "auto",
      }}
    >
      {groups.map((group, gi) => {
        const groupKey = group.label ?? `g${gi}`;
        const isCollapsed = group.collapsible && collapsed[groupKey];
        const configuredCount = group.items.filter((i) => i.configured).length;
        return (
        <Box
          key={groupKey}
          sx={{ display: "flex", flexDirection: "column", gap: "3px" }}
        >
          {group.label && (
            <Box
              onClick={
                group.collapsible
                  ? () =>
                      setCollapsed((c) => ({ ...c, [groupKey]: !c[groupKey] }))
                  : undefined
              }
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "2px",
                px: "10px",
                pt: gi === 0 ? 0 : "6px",
                pb: "2px",
                cursor: group.collapsible ? "pointer" : "default",
                userSelect: "none",
              }}
            >
              {group.collapsible &&
                (isCollapsed ? (
                  <CollapsedIcon sx={{ fontSize: 15, color: rg.text3, ml: "-4px" }} />
                ) : (
                  <ExpandedIcon sx={{ fontSize: 15, color: rg.text3, ml: "-4px" }} />
                ))}
              <Typography
                sx={{
                  fontSize: "10px",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 700,
                  color: rg.text3,
                  flex: 1,
                }}
              >
                {group.label}
              </Typography>
              {isCollapsed && configuredCount > 0 && (
                <Box
                  component="span"
                  className="rg-mono"
                  sx={{
                    fontSize: "10px",
                    fontWeight: 700,
                    color: rg.accent,
                    px: "5px",
                    borderRadius: "6px",
                    backgroundColor: rg.accentWeak,
                  }}
                >
                  {configuredCount}
                </Box>
              )}
            </Box>
          )}
          {!isCollapsed &&
          group.items.map((item) => {
            const active = item.key === selected;
            return (
              <Box
                key={item.key}
                onClick={() => onSelect(item.key)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  p: "8px 10px",
                  borderRadius: "9px",
                  cursor: "pointer",
                  backgroundColor: active ? rg.bgElev : "transparent",
                  "&:hover": active ? undefined : { backgroundColor: rg.bgElev },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    width: 20,
                    height: 20,
                    flex: "none",
                    display: "grid",
                    placeItems: "center",
                    borderRadius: "50%",
                    border: `1px solid ${
                      item.configured
                        ? rg.accent
                        : active
                          ? rg.accent
                          : rg.borderStrong
                    }`,
                    backgroundColor: item.configured ? rg.accent : "transparent",
                    color: item.configured ? "#ffffff" : rg.text3,
                  }}
                >
                  {item.configured && <CheckIcon sx={{ fontSize: 13 }} />}
                </Box>
                <Typography
                  sx={{
                    fontSize: "12.5px",
                    fontWeight: active || item.configured ? 600 : 500,
                    color: active ? rg.text : rg.text2,
                    lineHeight: 1.3,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.title}
                </Typography>
              </Box>
            );
          })}
        </Box>
        );
      })}
    </Box>
  );
};

export default ProviderRail;
